import type { paths } from "@src/packages/openapi/schema";

type FETProcedure =
  paths["/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

/**
 * Whether the transfer on record is hers to correct.
 *
 * `sourceKind` is optional on the PATCH, so sending `patient_reported` over a
 * row a clinic authored would relabel the clinic's record as hers and overwrite
 * its dates — the same thing `care.beta_result` refuses for a clinic-authored
 * beta. The self-report endpoint is no way around it either: it answers a replay
 * by returning the dated procedure untouched, which discards her edit in
 * silence.
 *
 * Deliberately "is this hers", not "is this the clinic's". A `document_parse` or
 * `system` row is neither, and the save path refuses those too; a screen that
 * locked only on `provider_entered` would leave them saving into nothing, which
 * is the silence this predicate exists to end. Both callers — the save and the
 * lock the patient sees — read this one function so they cannot drift apart.
 */
export function isPatientOwnedFETProcedure(
  procedure: Pick<FETProcedure, "sourceKind"> | null | undefined,
) {
  return procedure?.sourceKind === "patient_reported";
}

/**
 * Whether the transfer on record counts as reported.
 *
 * The one definition of "this transfer happened and we know when". Both the step
 * the hub shows as complete and the lock over a clinic's record read it, because
 * a screen that locks on a wider set than it completes shows neither: the lock
 * lives in the completed-step panel, so a row this called clinic-owned but not
 * reported rendered the entry form instead, with no lock and no provenance line,
 * and Confirm went back to saving into nothing.
 *
 * A `scheduled` or `rescheduled` row is not a report, however dated: the
 * backend's beta-order gate and `isFETWaitEligible` both refuse it, and showing
 * it as a confirmed transfer left her with no way to record a beta and no way to
 * end the cycle.
 */
export function isReportedFETTransfer<
  T extends Pick<FETProcedure, "status" | "recordStatus" | "actualDate">,
>(
  procedure: T | null | undefined,
): procedure is T & { actualDate: NonNullable<T["actualDate"]> } {
  return (
    procedure != null &&
    procedure.actualDate != null &&
    procedure.recordStatus === "current" &&
    (procedure.status === "awaiting_report" || procedure.status === "performed")
  );
}
